import {Controller} from '@hotwired/stimulus';

export default class extends Controller {
    static targets = ['nav', 'section'];

    static values = {
        offset: {type: Number, default: 0},
        activeClass: {type: String, default: 'active'},
    };

    connect() {
        this.linksById = new Map();
        this.lastScrollY = window.scrollY;
        this.currentId = null;
        this.scrollingToId = null; // Sección destino durante un click.
        this.scrollTimer = null;

        this.navTarget.querySelectorAll('a[href^="#"]').forEach((link) => {
            const id = link.getAttribute('href').slice(1);
            if (id) {
                this.linksById.set(id, link);
                // Escuchar clicks en los links del nav.
                link.addEventListener('click', this.onLinkClick);
            }
        });

        this.observer = new IntersectionObserver(
            (entries) => this.onIntersect(entries),
            {
                rootMargin: `-${this.offsetValue + 1}px 0px -60% 0px`,
                threshold: 0,
            }
        );

        this.sectionTargets.forEach((section) => this.observer.observe(section));
        this.syncFromHash();
    }

    disconnect() {
        this.observer?.disconnect();
        clearTimeout(this.scrollTimer);
        this.navTarget.querySelectorAll('a[href^="#"]').forEach((link) => {
            link.removeEventListener('click', this.onLinkClick);
        });
    }

    // Arrow function para mantener el contexto de `this`.
    onLinkClick = (event) => {
        const id = event.currentTarget.getAttribute('href').slice(1);
        if (!id) return;

        // Activar el tab de inmediato, sin esperar al observer.
        this.activate(id);

        // Marcar que estamos en un scroll programático hacia este id.
        // El observer ignorará callbacks hasta que el scroll termine.
        this.scrollingToId = id;
        clearTimeout(this.scrollTimer);

        // El timeout cubre la duración del scroll-behavior: smooth.
        // Ajusta si usas una duración de animación diferente.
        this.scrollTimer = setTimeout(() => {
            this.scrollingToId = null;
        }, 1000);
    };

    onIntersect(entries) {
        // Si hay un scroll por click en curso, el observer no interviene.
        if (this.scrollingToId !== null) return;

        const scrollingDown = window.scrollY >= this.lastScrollY;
        this.lastScrollY = window.scrollY;

        entries.forEach((entry) => {
            if (scrollingDown && entry.isIntersecting) {
                this.activate(entry.target.id);
            } else if (!scrollingDown && !entry.isIntersecting && entry.boundingClientRect.top > 0) {
                const prev = this.getPrevSection(entry.target);
                if (prev) this.activate(prev.id);
            }
        });
    }

    getPrevSection(current) {
        const sections = this.sectionTargets;
        const idx = sections.indexOf(current);
        return idx > 0 ? sections[idx - 1] : null;
    }

    activate(sectionId) {
        if (sectionId === this.currentId) return;

        const incoming = this.linksById.get(sectionId);
        if (!incoming) return;

        if (this.currentId) {
            const outgoing = this.linksById.get(this.currentId);
            if (outgoing) {
                outgoing.classList.remove(this.activeClassValue);
                outgoing.removeAttribute('aria-current');
            }
        }

        incoming.classList.add(this.activeClassValue);
        incoming.setAttribute('aria-current', 'true');
        this.currentId = sectionId;
    }

    syncFromHash() {
        const hash = window.location.hash.slice(1);
        const firstId = this.linksById.keys().next().value;
        this.activate(hash && this.linksById.has(hash) ? hash : firstId);
    }
}
